import React from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';
import { Link } from '~/lib/react-router-dom';

import { Box, Container, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import { expandTabRoutes, preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';

import APReportsTab, { items as apReportItems } from './APReportsPage';
import AgentHubPage, { items as agentHubItems } from './AgentHubPage';
import BankFilesTab, { items as bankFileItems } from './BankFilesPage';
import BedBankTab, { items as bedbankItems } from './BedBankPage';
import BusinessReportsTab, { items as businessReportItems } from './BusinessReportsPage';
import CarHireTab, { items as carHireItems } from './CarHirePage';
import CruisesTab, { items as cruiseItems } from './CruisePage';
import FlightsTab, { items as flightItems } from './FlightPage';
import FxRatesTab from './FxRates/FxRatesPage';
import LuxPlusTab, { items as luxPlusItems } from './LuxPlusPage';
import MonthReportsTab from './MonthReportsContainer';
import MusementTab, { items as musementItems } from './MusementPage';
import OfferSearchTab from './OffersSearch';
import OthersTab, { items as otherItems } from './Other/OtherPageContainer';
import ToursTab, { items as tourItems } from './TourPage';
import VendorPaymentsTab, { items as vendorPaymentItems } from './VendorPayments/VendorPaymentsPage';

export const TabsMap = {
  'offer-search': {
    label: 'Offer Search',
    slug: 'offer-search',
    component: OfferSearchTab,
  },
  'business-reports': {
    label: 'Business Reports',
    slug: 'business-reports',
    component: BusinessReportsTab,
  },
  'ap-reports': {
    label: 'AP Reports',
    slug: 'ap-reports',
    component: APReportsTab,
  },
  'month-end-reports': {
    label: 'Month End Reports',
    slug: 'month-end-reports',
    component: MonthReportsTab,
  },
  'vendor-payments': {
    label: 'Vendor Payments',
    slug: 'vendor-payments',
    component: VendorPaymentsTab,
  },
  'bank-files': {
    label: 'Bank Files',
    slug: 'bank-files',
    component: BankFilesTab,
  },
  'fx-rates': {
    label: 'FX Rates',
    slug: 'fx-rates',
    component: FxRatesTab,
  },
  bedbank: {
    label: 'BedBank',
    slug: 'bedbank',
    component: BedBankTab,
  },
  musement: {
    label: 'Musement',
    slug: 'musement',
    component: MusementTab,
  },
  flights: {
    label: 'Flights',
    slug: 'flights',
    component: FlightsTab,
  },
  tours: {
    label: 'Tours',
    slug: 'tours',
    component: ToursTab,
  },
  cruises: {
    slug: 'cruises',
    label: 'Cruises',
    component: CruisesTab,
  },
  carHire: {
    slug: 'car-hire',
    label: 'Car Hire',
    component: CarHireTab,
  },
  LuxPlus: {
    label: 'Lux Plus+',
    slug: 'lux-plus',
    component: LuxPlusTab,
  },
  AgentHub: {
    label: 'AgentHub',
    slug: 'agent-hub',
    component: AgentHubPage,
  },
  other: {
    label: 'Other',
    slug: 'other',
    component: OthersTab,
  },
} as const;

type TabKey = keyof typeof TabsMap;

const allTabs = Object.keys(TabsMap);

type SidebarItem = { readonly title: string; readonly slug?: string };

function financeSubItems(tabSlug: string, items: readonly SidebarItem[]) {
  const tabLabel = Object.values(TabsMap).find((t) => t.slug === tabSlug)?.label ?? tabSlug;
  return items.map((item) => ({
    path: `/finance/${tabSlug}/${item.slug ?? item.title}`,
    label: `Finance / ${tabLabel} / ${item.title}`,
    section: 'Finance',
  }));
}

// Pre-register at module load time so routes are searchable before visiting
preRegisterSpotlightRoutes(expandTabRoutes('/finance', 'Finance', TabsMap));
preRegisterSpotlightRoutes([
  ...financeSubItems('bank-files', bankFileItems),
  ...financeSubItems('ap-reports', apReportItems),
  ...financeSubItems('business-reports', businessReportItems),
  ...financeSubItems('vendor-payments', vendorPaymentItems),
  ...financeSubItems('bedbank', bedbankItems),
  ...financeSubItems('musement', musementItems),
  ...financeSubItems('flights', flightItems),
  ...financeSubItems('tours', tourItems),
  ...financeSubItems('cruises', cruiseItems),
  ...financeSubItems('car-hire', carHireItems),
  ...financeSubItems('lux-plus', luxPlusItems),
  ...financeSubItems('agent-hub', agentHubItems),
  ...financeSubItems('other', otherItems),
]);

export default function FinancePageContainer() {
  const { section = 'offer-search' } = useParams<{ section?: TabKey }>();

  const currentTab = TabsMap[section];

  const title = `Finance / ${currentTab.label}`;

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <PageHeader title={title} />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={section} variant="scrollable" scrollButtons="auto">
          {allTabs.map((tab) => (
            <Tab
              key={tab}
              value={tab}
              label={TabsMap[tab].label}
              to={`/finance/${tab}`}
              component={Link}
              sx={{
                minWidth: 30,
                padding: '12px 10px',
              }}
            />
          ))}
        </Tabs>
      </Box>

      <Box mt={4}>
        <Box component={currentTab.component} />
      </Box>
    </Container>
  );
}
